import { ProviderTypes } from '../common/Constants';
import { getNetworkIdsForPES } from './user.utils';

export interface CoverageTypeInput {
  coverageType: string;
  reciprocityIds: string[];
}

export interface ProviderTypeToRoleCodeMapping {
  providerType: string;
  roleType: string;
  roleCode: string;
}

export const PROVIDER_TYPE_ROLE_CODE_MAP: ProviderTypeToRoleCodeMapping[] = [
  {
    providerType: 'PRACTITIONER',
    roleType: 'Primary Care Physician',
    roleCode: '1',
  },
  {
    providerType: 'PRACTITIONER',
    roleType: 'Specialty Care Physician',
    roleCode: '2',
  },
  { providerType: 'ORGANIZATION', roleType: 'Hospital', roleCode: '3' },
  { providerType: 'ORGANIZATION', roleType: 'Facility', roleCode: '4' },
  { providerType: 'MEDICAL_GROUP', roleType: 'Medical Group', roleCode: '9' },
  {
    providerType: 'ORGANIZATION',
    roleType: 'Primary Care Facility',
    roleCode: '12',
  },
  {
    providerType: 'MEDICAL_GROUP',
    roleType: 'Primary Care Medical Group',
    roleCode: '13',
  },
];

export const buildCoverageTypesArray = (
  currentMember: any,
  featureFlags: any[],
  primaryCoverageType?: string
): CoverageTypeInput[] => {
  const coverageTypes: CoverageTypeInput[] = [];

  if (primaryCoverageType) {
    const reciprocityIds = getNetworkIdsForPES(
      currentMember,
      primaryCoverageType,
      featureFlags
    );

    return [
      {
        coverageType: primaryCoverageType,
        reciprocityIds: reciprocityIds
          ? reciprocityIds.split(',').map((id) => id.trim())
          : [],
      },
    ];
  }

  const memberEligibility = currentMember?.eligibility || [];
  const coverageSet = new Set<string>();

  memberEligibility.forEach((eligibility: any) => {
    const coverageType = eligibility?.memberHealthCoverage?.coverageType;
    if (Array.isArray(coverageType)) {
      coverageType.forEach((ct: any) => {
        if (ct?.coverageTypeCode) {
          coverageSet.add(ct.coverageTypeCode);
        }
      });
    }
  });

  coverageSet.forEach((coverage) => {
    const reciprocityIds = getNetworkIdsForPES(
      currentMember,
      coverage,
      featureFlags
    );

    coverageTypes.push({
      coverageType: coverage,
      reciprocityIds: reciprocityIds
        ? reciprocityIds.split(',').map((id) => id.trim())
        : [],
    });
  });

  return coverageTypes;
};

export const buildProviderTypesArray = (options: {
  searchAllTypes?: boolean;
  providerType?: string;
  sectionType?: string;
  chipValue?: string;
  isCommonProviderSearches?: boolean;
}): string[] => {
  const {
    searchAllTypes = false,
    providerType,
    sectionType,
    isCommonProviderSearches = false,
  } = options;

  if (searchAllTypes || isCommonProviderSearches || !providerType) {
    return [
      ProviderTypes.PRACTITIONER,
      ProviderTypes.ORGANIZATION,
      ProviderTypes.MEDICAL_GROUP,
    ];
  }

  if (providerType) {
    return [providerType.toUpperCase()];
  }

  if (sectionType) {
    const sectionTypeMapping: { [key: string]: string } = {
      practitioner: ProviderTypes.PRACTITIONER,
      organization: ProviderTypes.ORGANIZATION,
      medical_group: ProviderTypes.MEDICAL_GROUP,
      facility: ProviderTypes.ORGANIZATION,
      hospital: ProviderTypes.ORGANIZATION,
    };

    const mappedType = sectionTypeMapping[sectionType.toLowerCase()];
    if (mappedType) {
      return [mappedType];
    }
  }

  return [
    ProviderTypes.PRACTITIONER,
    ProviderTypes.ORGANIZATION,
    ProviderTypes.MEDICAL_GROUP,
  ];
};

export const mapProviderTypesToRoleCodes = (
  providerTypes: string[],
  isPCP: boolean = false,
  isPCPLandingPage: boolean = false
): string[] => {
  const roleCodes: string[] = [];

  providerTypes.forEach((providerType) => {
    const normalizedType = providerType.toUpperCase();

    if (isPCP || isPCPLandingPage) {
      if (normalizedType === 'PRACTITIONER') {
        roleCodes.push('1');
      } else if (normalizedType === 'ORGANIZATION') {
        roleCodes.push('12');
      } else if (normalizedType === 'MEDICAL_GROUP') {
        roleCodes.push('13');
      }
    } else {
      if (normalizedType === 'PRACTITIONER') {
        roleCodes.push('1', '2');
      } else if (normalizedType === 'ORGANIZATION') {
        roleCodes.push('3', '4');
      } else if (normalizedType === 'MEDICAL_GROUP') {
        roleCodes.push('9');
      }
    }
  });

  return Array.from(new Set(roleCodes));
};

export const getSearchNameForV2 = (
  search: string,
  searchType?: string
): { search?: string; providerName?: string } => {
  if (!search) {
    return {};
  }

  if (searchType === 'name') {
    return { providerName: search };
  }

  return { search };
};

export const parseProviderV2Result = (provider: any) => {
  if (!provider) return provider;

  const primaryNetwork = provider.networks?.[0];

  return {
    ...provider,
    specialities: primaryNetwork?.specialities || [],
    primarySpecialities: primaryNetwork?.primarySpecialities || [],
    networkId: primaryNetwork?.networkId,
    networkStartDate: primaryNetwork?.networkStartDate,
    networkEndDate: primaryNetwork?.networkEndDate,
    acceptingNewPatients: primaryNetwork?.acceptingNewPatients,
    pcpId: primaryNetwork?.pcpId,
    hospitalAffiliations: primaryNetwork?.hospitalAffiliations,
    providerGroupsAffiliations: primaryNetwork?.providerGroupsAffiliations,
    networks: provider.networks || [],
  };
};

export const filterSearchResultsV2 = (searchResult: any) => {
  if (!searchResult) return {};

  const providers = searchResult.providers?.map(parseProviderV2Result) || [];

  return {
    ...searchResult,
    providers,
  };
};
